/**
 * Store Mapper Application
 * Handles store search functionality with map integration
 */
const SUIT_TUX_RE = /\b(suit|suits|tux|tuxs|tuxedo|tuxedos)\b/i;
const MEN_RE = /\b(men|mens)\b/i;
const WOMEN_RE = /\bwomen\b/i;
const BRIDAL_RE = /\b(bridal|bridals|bride|brides|bridesmaids|gown|gowns|dress|dresses)\b/i;

class StoreMapper {
  constructor() {
    this.storemapperDiv = document.getElementById("storemapper");
    this.pageType = window.storemapperPageType || "";
    this.observers = new Map();
    this.processedElements = new WeakSet();
    this.initialized = false;

    const urlParams = new URLSearchParams(window.location.search);
    this.utmParams = {};
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith("utm_")) {
        this.utmParams[key] = value;
      }
    }

    // Tracks whether user triggered a valid search intent.
    // Used to show country-only results when zip is intentionally empty.
    this.hasExecutedSearch = false;
    this.resetVisibilityBound = false;
    this.lastCountryFallbackValue = "";
    this.filterValidationGuardBound = false;
    this.debugValidation = !!window.storemapperDebugValidation;
    this.debugCountryFilter = window.storemapperDebugCountryFilter !== false;
    this.countryFilterAutoExpandAttempts = 0;
    this.maxCountryFilterAutoExpandAttempts = 8;
    this.manualShowMoreClicks = 0;
    this.countryFilterBatchSize = 4;

    this.init();
  }

  /**
   * Get URL parameter by name
   * @param {string} name - Parameter name
   * @returns {string|null} Parameter value
   */
  getUrlParam(name) {
    const url = new URL(window.location.href);
    return url.searchParams.get(name);
  }

  /**
   * Initialize the application
   */
  init() {
    if (!this.storemapperDiv) return;

    // First check if storemapper is already loaded
    if (this.isStoremapperReady()) {
      this.setupApplication();
      return;
    }

    // Set up observer for dynamic loading
    const observer = new MutationObserver(() => {
      if (this.isStoremapperReady() && !this.initialized) {
        this.setupApplication();
        observer.disconnect();
      }
    });

    observer.observe(this.storemapperDiv, {
      childList: true,
      subtree: true,
    });

    // Fallback: Also try to initialize after a delay
    setTimeout(() => {
      if (this.isStoremapperReady() && !this.initialized) {
        this.setupApplication();
        observer.disconnect();
      }
    }, 1000);
  }

  /**
   * Check if storemapper is ready with actual content
   */
  isStoremapperReady() {
    if (!this.storemapperDiv || this.storemapperDiv.innerHTML.trim() === "") {
      return false;
    }

    // Check for key storemapper elements that indicate it's fully loaded
    const searchButton = document.getElementById("storemapper-go");
    const searchBox = document.getElementById("storemapper-zip");
    const mapContainer = document.querySelector(
      ".strmpr-layout-horizontal .strmpr-map",
    );

    // Storemapper is ready if core elements exist
    return !!(searchButton && searchBox && mapContainer);
  }

  /**
   * Setup the complete application
   */
  setupApplication() {
    if (this.initialized) return;

    this.addPageTypeClasses();
    this.setupElements();
    this.setupEventListeners();
    this.bindInternalValidationMessageSuppressor();
    this.setupFilters();
    this.filterDropdownOptions();
    this.setupInitialState();
    this.processUrlParameters();
    this.arrangeDivs();
    this.addInputLabels();

    // Set initial map design
    this.handleMapDesign();
    if (this.pageType === "designer-event") {
      this.handleCustomFunctionalities();
    }

    this.initialized = true;
  }

  /**
   * Add page type specific classes
   */
  addPageTypeClasses() {
    if (this.pageType === "designer-event") {
      this.storemapperDiv.classList.add("strmpr-designer-event");
    } else if (this.pageType === "find-retailer") {
      this.storemapperDiv.classList.add("strmpr-find-retailer");
    }
  }

  /**
   * Setup DOM elements references
   */
  setupElements() {
    this.elements = {
      searchButton: document.getElementById("storemapper-go"),
      searchzipInput: document.getElementById("storemapper-zip"),
      countrySelect: document.getElementById("storemapper-country"),
      viewMoreButton: document.querySelector(".strmpr-view-more-stores-button"),
      resetButton: document.querySelector(
        ".strmpr-search .strmpr-search-box-wrapper .strmpr-search-reset",
      ),
      storemapperResults: document.querySelector(
        ".strmpr-layout-horizontal .strmpr-search-results",
      ),
      mapContainer: document.querySelector(
        ".strmpr-layout-horizontal .strmpr-map",
      ),
      pageHeader: document.querySelector(".main-page-title"),
      filterBucket: document.querySelector(".strmpr-filter-bucket"),
    };
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    const { searchButton, searchzipInput, resetButton } = this.elements;

    // Checkbox change events
    document.addEventListener("change", (e) => {
      if (
        e.target.classList.contains("storemapper-filter-checkbox") ||
        e.target.closest(".strmpr-dropdown") ||
        e.target.closest(".strmpr-search-result-filters")
      ) {
        this.handleCustomFunctionalities();
      }

      // Re-evaluate search readiness if the country dropdown changes.
      if (
        this.pageType === "find-retailer" &&
        this.isCountryControlElement(e.target)
      ) {
        this.toggleSearchButton();
        this.syncCriteriaValidationState();
      }
    });

    // Search related events
    if (searchzipInput) {
      if (this.pageType === "find-retailer") {
        this.toggleSearchButton();
        searchzipInput.addEventListener("input", () => {
          this.clearSearchValidationError();
          this.toggleSearchButton();
          this.syncCriteriaValidationState();
        });
      }
    }

    if (this.pageType === "find-retailer") {
      this.bindCountryChangeListeners();
      this.bindCountrySelectAllSync();
      this.bindFacetSearchValidationGuard();
    }

    if (searchButton) {
      searchButton.addEventListener("click", (event) => {
        if (this.pageType === "find-retailer") {
          this.countryFilterAutoExpandAttempts = 0;
          this.manualShowMoreClicks = 0;
          const validation = this.validateSearchInputs();
          if (!validation.isValid) {
            this.hasExecutedSearch = false;
            this.showSearchValidationError(validation.message);
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }

          // If none is selected, apply "all" only for this search execution,
          // then restore UI to an unselected state.
          const restoreOptionalProductTypeState =
            this.ensureOptionalProductTypeSelection({ transient: true });

          // Storemapper can still enforce a non-empty location internally.
          // For country-only searches, use country as location fallback.
          const usedCountryFallback = this.applyCountryAsLocationFallback();
          this.clearSearchValidationError();
          this.hasExecutedSearch = true;

          if (typeof restoreOptionalProductTypeState === "function") {
            setTimeout(() => {
              restoreOptionalProductTypeState();
            }, 400);
          }

          if (usedCountryFallback) {
            this.clearCountryFallbackAfterSearch();
          }
        }

        this.handleMapDesignOnSearch();

        setTimeout(() => {
          this.handleCustomFunctionalities();
        }, 300);

        this.setupSearchResultsObserver();
      });
    }

    // Delegated: Storemapper can replace this button's node on re-render,
    // so a direct listener on the initially-captured element goes stale.
    document.addEventListener("click", (event) => {
      if (event.target.closest?.(".strmpr-view-more-stores-button")) {
        // Only count clicks that the user made, not synthetic auto-expand clicks.
        if (!this._isAutoExpandClick) {
          this.manualShowMoreClicks++;
        }
      }
    });

    if (resetButton) {
      resetButton.addEventListener("click", () => {
        this.hasExecutedSearch = false;
        this.countryFilterAutoExpandAttempts = 0;
        this.manualShowMoreClicks = 0;
        this.clearSearchValidationError();
        this.handleMapDesignOnReset();

        setTimeout(() => {
          this.handleCustomFunctionalities();
        }, 100);
      });
    }
  }

  /**
   * Toggle search button state based on zip input
   */
  toggleSearchButton() {
    const { searchButton } = this.elements;
    const hasCountry = this.getSelectedCountryCodes().length > 0;
    const hasZip = !!this.getNormalizedZipValue();

    // Search can run with country and/or zip.
    if (hasCountry || hasZip) {
      searchButton?.removeAttribute("disabled");
    } else {
      searchButton?.setAttribute("disabled", "");
    }
  }

  validateSearchInputs() {
    const selectedCountryCodes = this.getSelectedCountryCodes();
    const zipRaw = this.getNormalizedZipValue();
    const hasCountrySelection = selectedCountryCodes.length > 0;

    if (!hasCountrySelection && !zipRaw) {
      return {
        isValid: false,
        message: window.storemapperStrings?.selectCountryOrZip,
      };
    }

    if (!hasCountrySelection && zipRaw) {
      return { isValid: true, message: "" };
    }

    if (!zipRaw) {
      return { isValid: true, message: "" };
    }

    const detectedCountry = this.detectZipCountry(zipRaw);
    if (detectedCountry && !selectedCountryCodes.includes(detectedCountry)) {
      return {
        isValid: false,
        message: window.storemapperStrings?.postalCodeMismatch,
      };
    }

    // Keep validation permissive for undetected/variant postal formats.
    // Block only clear country mismatches detected from known patterns.

    return { isValid: true, message: "" };
  }

  applyCountryAsLocationFallback() {
    const selectedCountryCodes = this.getSelectedCountryCodes();
    const countryRaw =
      selectedCountryCodes[0] || this.getSelectedCountryValue();
    const zipRaw = this.getNormalizedZipValue();
    const searchInput = this.elements.searchzipInput;

    if (!searchInput || !countryRaw || zipRaw) {
      this.lastCountryFallbackValue = "";
      return false;
    }

    // Country-only search should not fail because Storemapper expects location text.
    const fallbackLocation =
      this.getCountryDisplayName(countryRaw) || countryRaw;
    this.lastCountryFallbackValue = fallbackLocation;
    searchInput.value = fallbackLocation;

    return true;
  }

  clearCountryFallbackAfterSearch() {
    const searchInput = this.elements.searchzipInput;
    const fallbackValue = this.lastCountryFallbackValue;
    if (!searchInput || !fallbackValue) return;

    // Clear only auto-injected value after click handlers finish.
    queueMicrotask(() => {
      if (searchInput.value === fallbackValue) {
        searchInput.value = "";
      }
      this.lastCountryFallbackValue = "";
    });
  }

  shouldApplyStrictCountryOnlyFilter() {
    if (this.pageType !== "find-retailer") return false;
    if (!this.hasExecutedSearch) return false;

    const selectedCountryCodes = this.getSelectedCountryCodes();
    const zipRaw = this.getNormalizedZipValue();

    return selectedCountryCodes.length > 0 && !zipRaw;
  }

  detectCountryCodeFromCountryLabel(text) {
    const countryLabelCodeMatch = text.match(
      /\bcountry\b[^a-z0-9]*(us|usa|united states|united states of america|uk|gb|gbr|united kingdom|canada|can)\b/i,
    );
    if (!countryLabelCodeMatch?.[1]) return "";

    const resolved = this.normalizeCountryCode(countryLabelCodeMatch[1]);
    return ["US", "UK", "CA"].includes(resolved) ? resolved : "";
  }

  matchCountryCodeFromPatterns(text, patternGroups) {
    for (const { code, patterns } of patternGroups) {
      for (const pattern of patterns) {
        if (pattern.test(text)) {
          return code;
        }
      }
    }

    return "";
  }

  detectCountryCodeFromFreeText(rawText) {
    const text = (rawText || "")
      .toString()
      .replace(/\u00A0/g, " ")
      .toLowerCase();
    if (!text) return "";

    const countryLabelCode = this.detectCountryCodeFromCountryLabel(text);
    if (countryLabelCode) return countryLabelCode;

    return this.matchCountryCodeFromPatterns(text, [
      { code: "CA", patterns: [/\b(canada)\b/i] },
      {
        code: "UK",
        patterns: [
          /united\s+kingdom/i,
          /\b(u\.?k\.?|uk)\b/i,
          /(,?\s*uk\s*$)|(,\s*uk\s*,)|(\buk\s*$)/i,
        ],
      },
      {
        code: "US",
        patterns: [
          /\b(united states(?: of america)?|u\.?s\.?a?\.?|\busa\b|\bus\b)\b/i,
        ],
      },
    ]);
  }

  resolveCountryFromResult(resultDiv) {
    const dedicatedSelectors = [
      ".strmpr-field-country",
      ".strmpr-field-custom.country",
      ".strmpr-field-custom.country_code",
      ".strmpr-field-custom.strmpr_country",
      ".strmpr-field-custom.country-name",
    ];
    const fallbackSelectors = [
      ".strmpr-field-address",
      ".strmpr-field-bucket-general",
    ];

    const diagnostics = [];

    const resolveCandidates = (candidates) => {
      for (const candidate of candidates) {
        if (!candidate) continue;

        const resolved = this.resolveCountryToken(candidate);
        const normalized = this.normalizeCountryCode(resolved);
        if (["US", "UK", "CA"].includes(normalized)) {
          return normalized;
        }

        const inferredFreeText = this.detectCountryCodeFromFreeText(candidate);
        if (inferredFreeText) return inferredFreeText;
      }
      return "";
    };

    for (const selector of dedicatedSelectors) {
      const fields = Array.from(resultDiv.querySelectorAll(selector));
      for (const field of fields) {
        const rawText = (field.textContent || "").trim();
        diagnostics.push({ source: selector, rawText });
        if (!rawText) continue;

        const afterColon = rawText.includes(":")
          ? rawText.split(":").slice(1).join(":").trim()
          : rawText;
        const code = resolveCandidates([afterColon, rawText]);
        if (code) return { code, diagnostics };
      }
    }

    const customFields = Array.from(
      resultDiv.querySelectorAll(
        '.strmpr-field-custom[class*="country"], [data-country], [data-country-code]',
      ),
    );
    for (const field of customFields) {
      const rawText = (field.textContent || "").trim();
      const attrCandidates = [
        field.getAttribute("data-country"),
        field.getAttribute("data-country-code"),
        field.dataset?.country,
        field.dataset?.countryCode,
      ].filter(Boolean);

      diagnostics.push({
        source: "custom-country-field",
        rawText: `${rawText} | attrs: ${attrCandidates.join(" | ")}`,
      });

      const code = resolveCandidates([rawText, ...attrCandidates]);
      if (code) return { code, diagnostics };
    }

    for (const selector of fallbackSelectors) {
      const fields = Array.from(resultDiv.querySelectorAll(selector));
      for (const field of fields) {
        const rawText = (field.textContent || "").trim();
        diagnostics.push({ source: selector, rawText });

        const inferred = this.detectCountryCodeFromFreeText(rawText);
        if (inferred) {
          return { code: inferred, diagnostics };
        }
      }
    }

    const idx = resultDiv.getAttribute("data-idx") || "";
    if (idx) {
      const mapView = document.querySelector(
        `.storemapper_view[data-idx="${idx}"]`,
      );
      if (mapView) {
        const mapText = (mapView.textContent || "").trim();
        diagnostics.push({ source: "map-view", rawText: mapText });

        const mapCandidates = [
          mapText,
          mapView.getAttribute("data-country"),
          mapView.getAttribute("data-country-code"),
          mapView.dataset?.country,
          mapView.dataset?.countryCode,
        ].filter(Boolean);

        const mapCode = resolveCandidates(mapCandidates);
        if (mapCode) {
          return { code: mapCode, diagnostics };
        }
      }
    }

    const fullText = (resultDiv.textContent || "").trim();
    diagnostics.push({ source: "result-text", rawText: fullText });

    // Try general free text detection first
    const freeTextCode = this.detectCountryCodeFromFreeText(fullText);
    if (freeTextCode) {
      return { code: freeTextCode, diagnostics };
    }

    return {
      code: "",
      diagnostics,
    };
  }

  /**
   * Storemapper paginates by proximity, not country, so the first page(s)
   * can legitimately contain zero matches for the selected country. Click
   * "Show more" automatically until a match is found or results are exhausted.
   */
  tryAutoExpandForCountryMatches() {
    const viewMoreButton = document.querySelector(
      ".strmpr-view-more-stores-button",
    );

    const isVisible = !!viewMoreButton?.offsetParent;
    const isDisabled =
      viewMoreButton?.disabled ||
      viewMoreButton?.classList.contains("disabled") ||
      viewMoreButton?.classList.contains("hidden");

    const canExpand =
      !!viewMoreButton &&
      isVisible &&
      !isDisabled &&
      this.countryFilterAutoExpandAttempts <
        this.maxCountryFilterAutoExpandAttempts;

    if (!canExpand) return false;

    this.countryFilterAutoExpandAttempts += 1;
    this._isAutoExpandClick = true;
    viewMoreButton.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );
    this._isAutoExpandClick = false;
    return true;
  }

  toggleNoCountryMatchMessage(show) {
    const { storemapperResults } = this.elements;
    if (!storemapperResults) return;

    let messageEl = document.getElementById("strmpr-country-no-match");

    if (!show) {
      messageEl?.classList.add("hidden");
      return;
    }

    if (!messageEl) {
      messageEl = this.createElement(
        "p",
        "strmpr-country-no-match",
        [],
        window.storemapperStrings?.noStoresForCountry ||
          "No stores found for the selected country.",
      );
      messageEl.id = "strmpr-country-no-match";
      storemapperResults.insertAdjacentElement("afterbegin", messageEl);
    }
    messageEl.classList.remove("hidden");
  }

  applyStrictCountryOnlyFilter() {
    const results = Array.from(
      document.querySelectorAll(".strmpr-search-result[data-idx]"),
    );
    if (!results.length) return;

    if (!this.shouldApplyStrictCountryOnlyFilter()) {
      this.countryFilterAutoExpandAttempts = 0;
      this.toggleNoCountryMatchMessage(false);
      results.forEach((resultDiv) => {
        resultDiv.classList.remove("strmpr-country-filter-hidden");
        resultDiv.style.removeProperty("display");

        const idx = resultDiv.getAttribute("data-idx");
        if (!idx) return;

        const mapView = document.querySelector(
          `.storemapper_view[data-idx="${idx}"]`,
        );
        mapView?.classList.remove("strmpr-country-filter-hidden");
        mapView?.style.removeProperty("display");
      });
      return;
    }

    const selectedCountries = this.getSelectedCountryCodes();
    const allowedCountries = new Set(selectedCountries);
    const diagnostics = [];

    const computedRows = results.map((resultDiv) => {
      const idx = resultDiv.getAttribute("data-idx") || "";
      const { code, diagnostics: sourceDiagnostics } =
        this.resolveCountryFromResult(resultDiv);
      return { resultDiv, idx, code, sourceDiagnostics };
    });

    const matchCount = computedRows.filter((row) =>
      allowedCountries.has(row.code),
    ).length;
    const hasAnyMatch = matchCount > 0;
    // Each manual "Show More" click raises the target by one batch.
    const minVisibleStores =
      (this.manualShowMoreClicks + 1) * this.countryFilterBatchSize;

    if (
      matchCount < minVisibleStores &&
      this.tryAutoExpandForCountryMatches()
    ) {
      // Show already-matched results immediately; hide non-matches while more pages load.
      // This prevents previously visible stores from disappearing during auto-expand.
      computedRows.forEach(({ resultDiv, idx, code }) => {
        const isAllowed = !!code && allowedCountries.has(code);
        resultDiv.classList.toggle("strmpr-country-filter-hidden", !isAllowed);
        if (isAllowed) {
          resultDiv.style.removeProperty("display");
        } else {
          resultDiv.style.display = "none";
        }
        const mapView = document.querySelector(
          `.storemapper_view[data-idx="${idx}"]`,
        );
        if (mapView) {
          mapView.classList.toggle("strmpr-country-filter-hidden", !isAllowed);
          if (isAllowed) {
            mapView.style.removeProperty("display");
          } else {
            mapView.style.display = "none";
          }
        }
      });
      return;
    }

    this.countryFilterAutoExpandAttempts = 0;

    computedRows.forEach(({ resultDiv, idx, code, sourceDiagnostics }) => {
      const isAllowed = !!code && allowedCountries.has(code);

      resultDiv.classList.toggle("strmpr-country-filter-hidden", !isAllowed);
      if (isAllowed) {
        resultDiv.style.removeProperty("display");
      } else {
        resultDiv.style.display = "none";
      }

      const mapView = document.querySelector(
        `.storemapper_view[data-idx="${idx}"]`,
      );
      if (mapView) {
        mapView.classList.toggle("strmpr-country-filter-hidden", !isAllowed);
        if (isAllowed) {
          mapView.style.removeProperty("display");
        } else {
          mapView.style.display = "none";
        }
      }

      diagnostics.push({
        idx,
        extractedCountryCode: code || "unresolved",
        allowedCountries: selectedCountries.join(", "),
        passesFilter: isAllowed,
        sources: sourceDiagnostics
          .map((entry) => `${entry.source}: ${entry.rawText}`)
          .join(" | "),
      });
    });

    this.toggleNoCountryMatchMessage(!hasAnyMatch);
  }

  bindFacetSearchValidationGuard() {
    if (this.filterValidationGuardBound) return;

    // Capture phase blocks invalid facet-triggered searches before Storemapper reacts.
    document.addEventListener(
      "change",
      (event) => {
        if (this.pageType !== "find-retailer") return;

        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!this.isFacetFilterControlElement(target)) return;

        const validation = this.validateSearchInputs();
        if (!validation.isValid) {
          this.hasExecutedSearch = false;
          this.showSearchValidationError(validation.message);
          this.logValidationBlock("facet-change", validation.message, target);
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        this.clearSearchValidationError();
      },
      true,
    );

    this.filterValidationGuardBound = true;
  }

  logValidationBlock(source, message, targetElement) {
    if (!this.debugValidation) return;

    const debugMeta = {
      source,
      message,
      country: this.getSelectedCountryValue(),
      zip: this.getNormalizedZipValue(),
      targetClass: targetElement?.className || "",
      timestamp: new Date().toISOString(),
    };

    console.info("[StoreMapper Validation Blocked]", debugMeta);
  }

  isFacetFilterControlElement(element) {
    const insideFilters = !!element.closest(".strmpr-search-result-filters");
    if (!insideFilters) return false;

    // Allow country controls to update freely so users can resolve mismatch.
    return !this.isCountryControlElement(element);
  }

  getNormalizedZipValue() {
    return (this.elements.searchzipInput?.value || "").trim();
  }

  getSelectedCountryValue() {
    const selectedCountryCodes = this.getSelectedCountryCodes();
    if (selectedCountryCodes.length > 0) {
      return selectedCountryCodes[0];
    }

    const { countrySelect } = this.elements;

    if (countrySelect?.value) {
      const resolved = this.resolveCountryToken(countrySelect.value);
      if (resolved) return resolved;
    }

    const checkedCountryInputs = Array.from(
      document.querySelectorAll(
        '.strmpr-dropdown-country input[type="checkbox"]:checked, .strmpr-dropdown-country input[type="radio"]:checked',
      ),
    );
    for (const checkedCountryInput of checkedCountryInputs) {
      const option = checkedCountryInput.closest(".strmpr-dropdown-option");
      const labelText = this.getDropdownOptionText(option);

      const fromLabel = this.resolveCountryToken(labelText);
      if (fromLabel) return fromLabel;

      const fromValue = this.resolveCountryToken(checkedCountryInput.value);
      if (fromValue) return fromValue;
    }

    const checkedCountryOption = document.querySelector(
      ".strmpr-dropdown-country .strmpr-dropdown-option.checked",
    );
    if (checkedCountryOption) {
      const text = this.getDropdownOptionText(checkedCountryOption);
      const resolved = this.resolveCountryToken(text);
      if (resolved) return resolved;
    }

    const countryButtonSpan = document.querySelector(
      ".strmpr-dropdown-country .strmpr-dropdown-btn span",
    );
    const buttonText = countryButtonSpan?.textContent || "";
    const resolvedButton = this.resolveCountryToken(buttonText);
    if (resolvedButton) return resolvedButton;

    return "";
  }

  getSelectedCountryCodes() {
    const selected = new Set();

    const addCandidate = (token) => {
      const resolved = this.resolveCountryToken(token);
      if (!resolved) return;

      const normalizedCode = this.normalizeCountryCode(resolved);
      if (["US", "UK", "CA"].includes(normalizedCode)) {
        selected.add(normalizedCode);
      }
    };

    const { countrySelect } = this.elements;
    if (countrySelect?.value) {
      addCandidate(countrySelect.value);
    }

    const checkedCountryInputs = Array.from(
      document.querySelectorAll(
        '.strmpr-dropdown-country input[type="checkbox"]:checked, .strmpr-dropdown-country input[type="radio"]:checked',
      ),
    );

    checkedCountryInputs.forEach((input) => {
      const option = input.closest(".strmpr-dropdown-option");
      addCandidate(this.getDropdownOptionText(option));
      addCandidate(input.value);
    });

    const checkedCountryOptions = document.querySelectorAll(
      ".strmpr-dropdown-country .strmpr-dropdown-option.checked",
    );
    checkedCountryOptions.forEach((option) => {
      addCandidate(this.getDropdownOptionText(option));
    });

    const countryButtonSpan = document.querySelector(
      ".strmpr-dropdown-country .strmpr-dropdown-btn span",
    );
    addCandidate(countryButtonSpan?.textContent || "");

    return Array.from(selected);
  }

  getDropdownOptionText(optionElement) {
    if (!optionElement) return "";
    const label = optionElement.querySelector(
      "label, .strmpr-dropdown-option-label, .storemapper-category-link",
    );
    return (label?.textContent || optionElement.textContent || "").trim();
  }

  getDropdownCheckboxMeta(dropdownElement) {
    if (!dropdownElement) {
      return { master: null, regular: [], all: [] };
    }

    const all = Array.from(
      dropdownElement.querySelectorAll(
        '.strmpr-dropdown-option input[type="checkbox"]',
      ),
    )
      .map((checkbox) => {
        const option = checkbox.closest(".strmpr-dropdown-option");
        if (!option) return null;

        const label = option.querySelector(
          "label, .strmpr-dropdown-option-label, .storemapper-category-link",
        );
        const text = (label?.textContent || option.textContent || "")
          .toLowerCase()
          .trim();

        const isMaster =
          checkbox.classList.contains("storemapper-filter-all") ||
          checkbox.id.startsWith("storemapperFilterAll") ||
          text.includes("select all") ||
          text.includes("deselect all");

        return { checkbox, option, label, isMaster };
      })
      .filter(Boolean);

    const master = all.find((entry) => entry.isMaster) || null;
    const regular = all.filter((entry) => !entry.isMaster);

    return { master, regular, all };
  }

  syncDropdownMasterState(dropdownElement) {
    const { master, regular } = this.getDropdownCheckboxMeta(dropdownElement);
    if (!master || !regular.length) return;

    const everyChecked = regular.every((entry) => entry.checkbox.checked);
    master.checkbox.checked = everyChecked;
    master.option.classList.toggle("checked", everyChecked);

    if (master.label) {
      master.label.textContent = everyChecked ? "Deselect All" : "Select All";
    }
  }

  resolveCountryToken(value) {
    const raw = (value || "").toString().trim();
    if (!raw || this.isPlaceholderCountryValue(raw)) return "";

    const lowerRaw = raw.toLowerCase();
    if (lowerRaw.includes("select all") || lowerRaw.includes("deselect all")) {
      return "";
    }

    const normalizedCode = this.normalizeCountryCode(raw);
    if (["US", "UK", "CA"].includes(normalizedCode)) {
      return normalizedCode;
    }

    // Ignore internal ids or token-like values (e.g. numeric option ids).
    const looksLikeDisplayCountry = /^[a-z][a-z\s.'-]*$/i.test(raw);
    return looksLikeDisplayCountry ? raw : "";
  }

  isPlaceholderCountryValue(value) {
    const normalized = (value || "").toString().trim().toLowerCase();
    return (
      !normalized ||
      normalized === "country" ||
      normalized === "all" ||
      normalized === "select country" ||
      normalized === "select a country"
    );
  }

  normalizeCountryCode(value) {
    const normalized = (value || "").toString().trim().toLowerCase();

    const countryMap = {
      us: "US",
      usa: "US",
      "united states": "US",
      "united states of america": "US",
      uk: "UK",
      gb: "UK",
      gbr: "UK",
      "united kingdom": "UK",
      ca: "CA",
      can: "CA",
      canada: "CA",
    };

    return countryMap[normalized] || normalized.toUpperCase();
  }

  getCountryDisplayName(rawCountry) {
    const code = this.normalizeCountryCode(rawCountry);
    const names = {
      US: "United States",
      UK: "United Kingdom",
      CA: "Canada",
    };
    return names[code] || rawCountry;
  }

  getPostalPatternForCountry(countryCode) {
    switch (countryCode) {
      case "US":
        return /^\d{5}(?:-\d{4})?$/;
      case "UK":
        return /^(GIR\s?0AA|[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})$/i;
      case "CA":
        return /^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/i;
      default:
        return /^$/;
    }
  }

  detectZipCountry(zipValue) {
    const zip = (zipValue || "").trim();
    if (!zip) return "";

    return (
      ["US", "UK", "CA"].find((countryCode) =>
        this.getPostalPatternForCountry(countryCode).test(zip),
      ) || ""
    );
  }

  showSearchValidationError(message) {
    const errorElement = document.getElementById("storemapper-error");
    if (!errorElement) return;

    errorElement.textContent = message;
    errorElement.setAttribute("role", "alert");
    errorElement.setAttribute("aria-live", "polite");
  }

  clearSearchValidationError() {
    const errorElement = document.getElementById("storemapper-error");
    if (!errorElement) return;
    errorElement.textContent = "";
  }
  bindInternalValidationMessageSuppressor() {
    const errorElement = document.getElementById("storemapper-error");
    if (!errorElement || errorElement.dataset.internalValidationBound) return;

    const suppressInternalMessage = () => {
      const message = (errorElement.textContent || "").toLowerCase();
      const hasCountryOnlyCriteria =
        this.pageType === "find-retailer" &&
        this.getSelectedCountryCodes().length > 0 &&
        !this.getNormalizedZipValue();
      const genericInternalLocationMessage =
        /enter\s+a\s+location|please\s+enter\s+a\s+location|search\s+field/.test(
          message,
        );
      if (
        message.includes(window.storemapperStrings?.enterLocation) ||
        message.includes(window.storemapperStrings?.pleaseEnterLocation)||
        (hasCountryOnlyCriteria && genericInternalLocationMessage)
      ) {
        errorElement.textContent = "";
        errorElement.removeAttribute("role");
        errorElement.removeAttribute("aria-live");
        errorElement.classList.add("hidden");
      } else if (errorElement.textContent.trim()) {
        errorElement.classList.remove("hidden");
      }
    };

    suppressInternalMessage();

    const observer = new MutationObserver(suppressInternalMessage);
    observer.observe(errorElement, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    this.observers.set("validationError", observer);
    errorElement.dataset.internalValidationBound = "true";
  }

  bindCountryChangeListeners() {
    const countrySelect = this.elements.countrySelect;
    if (countrySelect && !countrySelect.dataset.searchButtonBound) {
      countrySelect.addEventListener("change", () => {
        this.toggleSearchButton();
        this.syncCriteriaValidationState();
      });
      countrySelect.dataset.searchButtonBound = "true";
    }

    const countryDropdown = document.querySelector(".strmpr-dropdown-country");
    if (countryDropdown && !countryDropdown.dataset.searchButtonBound) {
      countryDropdown.addEventListener("click", () => {
        setTimeout(() => {
          this.toggleSearchButton();
          this.syncCriteriaValidationState();
        }, 0);
      });
      countryDropdown.dataset.searchButtonBound = "true";
    }
  }

  bindCountrySelectAllSync() {
    const dropdowns = Array.from(
      document.querySelectorAll(
        ".strmpr-search-result-filters .strmpr-dropdown, .strmpr-dropdown-country",
      ),
    );

    dropdowns.forEach((dropdown) => {
      if (!(dropdown instanceof HTMLElement)) return;
      if (dropdown.dataset.selectAllSyncBound) return;

      let isBulkUpdating = false;
      let isSyncingMasterState = false;

      const getOptionMeta = () => this.getDropdownCheckboxMeta(dropdown);

      const setOptionState = (meta, checked) => {
        if (!meta) return;

        const hasCheckedClass = meta.option.classList.contains("checked");
        if (meta.checkbox.checked === checked && hasCheckedClass === checked) {
          return;
        }

        meta.checkbox.checked = checked;
        meta.option.classList.toggle("checked", checked);
      };

      const syncMasterState = () => {
        if (isSyncingMasterState) return;

        const { master, regular } = getOptionMeta();
        if (!master || !regular.length) return;

        isSyncingMasterState = true;
        try {
          this.syncDropdownMasterState(dropdown);
        } finally {
          isSyncingMasterState = false;
        }
      };

      const handleDropdownInteraction = (event) => {
        const target = event.target;
        if (
          !(target instanceof HTMLInputElement) ||
          !["checkbox", "radio"].includes(target.type)
        ) {
          return;
        }

        if (target.type === "radio") {
          this.clearSearchValidationError();
          if (dropdown.classList.contains("strmpr-dropdown-country")) {
            this.toggleSearchButton();
            this.syncCriteriaValidationState();
          }
          return;
        }

        const { master, regular } = getOptionMeta();
        if (!master || !regular.length) {
          if (dropdown.classList.contains("strmpr-dropdown-country")) {
            this.toggleSearchButton();
          }
          return;
        }

        if (isBulkUpdating) return;

        const targetIsMaster = target === master.checkbox;

        if (targetIsMaster) {
          const shouldCheckAll = master.checkbox.checked;
          isBulkUpdating = true;
          regular.forEach((meta) => setOptionState(meta, shouldCheckAll));
          isBulkUpdating = false;
          this.syncDropdownMasterState(dropdown);
          this.handleCustomFunctionalities();
        } else {
          syncMasterState();
        }

        this.clearSearchValidationError();
        if (dropdown.classList.contains("strmpr-dropdown-country")) {
          this.toggleSearchButton();
          this.syncCriteriaValidationState();
        }
      };

      const handleOptionContainerClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const option = target.closest(".strmpr-dropdown-option");
        if (!(option instanceof HTMLElement) || !dropdown.contains(option)) {
          return;
        }

        // Preserve native behavior for direct input/label/anchor clicks.
        if (target.closest("input, label, a, button, select, textarea")) {
          return;
        }

        const input = option.querySelector(
          'input[type="checkbox"], input[type="radio"]',
        );
        if (!(input instanceof HTMLInputElement) || input.disabled) return;

        if (input.type === "radio") {
          if (input.checked) return;
          input.checked = true;
        } else {
          input.checked = !input.checked;
        }

        input.dispatchEvent(new Event("change", { bubbles: true }));
      };

      dropdown.addEventListener("change", handleDropdownInteraction);
      dropdown.addEventListener("click", handleOptionContainerClick);

      // Defer once to sync initial state after Storemapper populates options.
      setTimeout(syncMasterState, 0);
      dropdown.dataset.selectAllSyncBound = "true";
    });
  }

  ensureOptionalProductTypeSelection(options = {}) {
    const { transient = false } = options;
    const productTypeDropdown = this.getProductTypeDropdownElement();
    if (!productTypeDropdown) return;

    const { master, regular, all } =
      this.getDropdownCheckboxMeta(productTypeDropdown);
    if (!regular.length) return;

    const hasAnyRegularSelected = regular.some(
      (entry) => entry.checkbox.checked,
    );
    if (hasAnyRegularSelected) return;

    const snapshot = all.map((entry) => {
      const option = entry.option;
      const label = entry.label;
      return {
        checkbox: entry.checkbox,
        checked: entry.checkbox.checked,
        option,
        label,
        labelText: label?.textContent || "",
        optionCheckedClass: !!option?.classList.contains("checked"),
      };
    });

    if (master) {
      if (!master.checkbox.checked) {
        master.checkbox.checked = true;
        master.checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } else {
      regular.forEach((entry) => {
        if (!entry.checkbox.checked) {
          entry.checkbox.checked = true;
          entry.checkbox.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    if (!transient) return;

    return () => {
      snapshot.forEach((entry) => {
        entry.checkbox.checked = entry.checked;
        if (!entry.option) return;

        if (entry.optionCheckedClass) {
          entry.option.classList.add("checked");
        } else {
          entry.option.classList.remove("checked");
        }

        if (entry.label) {
          entry.label.textContent = entry.labelText;
        }
      });

      this.syncDropdownMasterState(productTypeDropdown);
      this.setupTypeFilter();
    };
  }

  getProductTypeDropdownElement() {
    const directSelector = document.querySelector(
      ".strmpr-search-result-filters .strmpr-dropdown.strmpr-dropdown-product-type-event",
    );
    if (directSelector) return directSelector;

    const allDropdowns = Array.from(
      document.querySelectorAll(
        ".strmpr-search-result-filters .strmpr-dropdown",
      ),
    );

    return (
      allDropdowns.find((dropdown) => {
        const button = dropdown.querySelector(".strmpr-dropdown-btn span");
        const buttonText = (button?.textContent || "").toLowerCase();
        const classText = dropdown.className.toLowerCase();

        return (
          buttonText.includes("product type") ||
          classText.includes("product-type")
        );
      }) || null
    );
  }

  isCountryControlElement(element) {
    return (
      !!element.closest(".strmpr-dropdown-country") ||
      element.id === "storemapper-country"
    );
  }

  syncCriteriaValidationState() {
    if (this.pageType !== "find-retailer") return;

    const selectedCountryCodes = this.getSelectedCountryCodes();
    const zip = this.getNormalizedZipValue();

    // Criteria changed, so old results are stale until next explicit search.
    this.hasExecutedSearch = false;
    this.countryFilterAutoExpandAttempts = 0;
    this.hideSearchResultsAndPins();

    // Do not show validation for incomplete input while user is typing.
    if (!selectedCountryCodes.length || !zip) {
      this.clearSearchValidationError();
      return;
    }

    const validation = this.validateSearchInputs();
    if (validation.isValid) {
      this.clearSearchValidationError();
      return;
    }

    // Prevent stale results/map pins from looking like valid output.
    this.hasExecutedSearch = false;
    this.showSearchValidationError(validation.message);
    this.hideSearchResultsAndPins();
  }

  hideSearchResultsAndPins() {
    const { storemapperResults } = this.elements;
    const mapPins = document.querySelectorAll("gmp-advanced-marker");

    storemapperResults?.classList.add("hidden");
    mapPins.forEach((pin) => pin.classList.add("hidden"));
  }
  /**
   * Setup dropdown filters with classes
   */
  setupFilters() {
    const dropdownButtons = document.querySelectorAll(".strmpr-dropdown-btn");

    dropdownButtons.forEach((button) => {
      const span = button.querySelector("span");
      if (!span?.textContent) return;

      const text = span.textContent
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "-");

      const dropdownParent = button.closest(".strmpr-dropdown");
      dropdownParent?.classList.add(`strmpr-dropdown-${text}`);
    });

    this.setupTypeFilter();
  }

  /**
   * Filter dropdown options based on page type and storemapperCategories
   */
  filterDropdownOptions() {
    const dropdown = document.getElementById("storemapper-filter-drop-down-1");
    if (!dropdown) return;

    const options = dropdown.querySelectorAll(".strmpr-dropdown-option");
    if (!options.length) return;

    // Get categories from window global if available
    const categories = window.storemapperCategories || [];

    options.forEach((option) => {
      const label = option.querySelector("label");
      if (!label) return;

      const labelText = label.textContent.trim();
      const labelTextLower = labelText.toLowerCase();

      // Skip filtering for "Select All" option
      if (labelTextLower === "select all" || labelTextLower === "all") {
        return;
      }

      // Hide by default, show only if matches a category
      if (categories.length > 0) {
        const matchesCategory = categories.some(
          (category) =>
            category.toLowerCase() === labelTextLower ||
            labelTextLower.includes(category.toLowerCase()),
        );

        if (!matchesCategory) {
          option.style.display = "none";
        } else {
          option.style.display = "";
        }
      }
    });
  }

  /**
   * Setup type filter text based on page type
   */
  setupTypeFilter() {
    const typeFilter = document.querySelector(
      ".strmpr-search-result-filters .strmpr-dropdown.strmpr-dropdown-product-type-event button span",
    );

    if (!typeFilter) return;

    const originalText = typeFilter.textContent.trim();
    const splitTexts = originalText.split("/");

    if (splitTexts.length < 2) return;

    const productTypeText = splitTexts[0].trim();
    const eventText = splitTexts[1].trim();

    if (this.pageType === "designer-event") {
      typeFilter.textContent = eventText;
    } else if (this.pageType === "find-retailer") {
      typeFilter.textContent = productTypeText;
    }
  }

  /**
   * Setup initial state for find-retailer page
   */
  setupInitialState() {
    if (this.pageType !== "find-retailer") return;

    const { storemapperResults, mapContainer } = this.elements;

    // Hide results initially
    storemapperResults?.classList.add("hidden");

    // Hide existing map pins
    this.hideMapPins();

    // Setup observer for dynamically added pins
    if (mapContainer) {
      this.setupMapPinObserver(mapContainer);
    }
  }

  /**
   * Hide map pins
   */
  hideMapPins() {
    const mapPins = document.querySelectorAll("gmp-advanced-marker");
    mapPins.forEach((pin) => pin.classList.add("hidden"));
  }

  /**
   * Setup observer for map pins
   */
  setupMapPinObserver(mapContainer) {
    const observer = new MutationObserver(() => {
      const newPins = document.querySelectorAll(
        "gmp-advanced-marker:not(.observed)",
      );
      newPins.forEach((pin) => {
        pin.classList.add("hidden", "observed");
      });
    });

    observer.observe(mapContainer, {
      childList: true,
      subtree: true,
    });

    this.observers.set("mapPins", observer);
  }

  resolveCollectionNameFromParam(collectionParam) {
    if (!collectionParam) return null;

    const parts = collectionParam
      .toString()
      .split(/\s*(?:\|\||\||,)\s*/)
      .filter(Boolean);
    if (parts.length > 1) {
      for (const p of parts) {
        const candidate = this.resolveCollectionNameFromParam(p);
        if (candidate) return candidate;
      }
      return null;
    }

    try {
      const maybeUrl = new URL(collectionParam, window.location.origin);
      const qp =
        maybeUrl.searchParams.get("sm") ||
        maybeUrl.searchParams.get("collection");
      if (qp) collectionParam = qp;
    } catch (e) {
      /* ignore */
    }

    const normalize = (s) =>
      (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^\w\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    const paramNorm = normalize(collectionParam);

    let globalMap = window.storemapperCollectionMap;
    if (!globalMap || typeof globalMap !== "object") {
      try {
        const stored = JSON.parse(
          localStorage.getItem("storemapperCollectionMap") || "{}",
        );
        if (stored && typeof stored === "object") globalMap = stored;
      } catch (e) {
        /* ignore */
      }
    }
    if (globalMap && typeof globalMap === "object") {
      for (const [k, v] of Object.entries(globalMap)) {
        if (!k && !v) continue;
        const kNorm = normalize(k);
        const vNorm = normalize(v);
        if (kNorm === paramNorm) return k.toString();
        if (vNorm === paramNorm) return v.toString();
      }
      if (globalMap[paramNorm]) return globalMap[paramNorm];
    }

    const optionEls = Array.from(
      document.querySelectorAll(".strmpr-dropdown-option"),
    ).filter(Boolean);
    for (const opt of optionEls) {
      const labelEl = opt.querySelector(
        "label, .strmpr-dropdown-option-label, .storemapper-category-link",
      );
      const label = (labelEl?.textContent || opt.textContent || "").trim();
      const labelNorm = normalize(label);
      const dataKey = (opt.getAttribute("data-storemapper-key") || "")
        .toString()
        .trim()
        .toLowerCase();

      if (labelNorm === paramNorm || dataKey === paramNorm)
        return label || collectionParam;
    }

    const tokens = (t) => (t || "").split(/\s+/).filter(Boolean);
    const pTokens = tokens(paramNorm);
    if (pTokens.length) {
      let best = { score: 0, label: null };
      for (const opt of optionEls) {
        const labelEl = opt.querySelector(
          "label, .strmpr-dropdown-option-label, .storemapper-category-link",
        );
        const label = (labelEl?.textContent || opt.textContent || "").trim();
        const lNorm = normalize(label);
        const lTokens = tokens(lNorm);
        if (!lTokens.length) continue;

        const setA = new Set(pTokens);
        const setB = new Set(lTokens);
        const intersection = [...setA].filter((x) => setB.has(x)).length;
        const union = new Set([...pTokens, ...lTokens]).size || 1;
        const score = intersection / union;
        const boost = pTokens.some((pt) =>
          lTokens.some((lt) => lt.includes(pt) || pt.includes(lt)),
        )
          ? 0.1
          : 0;
        const finalScore = score + boost;
        if (finalScore > best.score) best = { score: finalScore, label };
      }
      if (best.label && best.score >= 0.22) return best.label;
    }

    return collectionParam;
  }

  processUrlParameters(retry = 0) {
    const rawSm = this.getUrlParam("sm") || this.getUrlParam("collection");
    if (!rawSm) return;

    const decodeAndSplit = (raw) => {
      try {
        raw = decodeURIComponent(raw.replace(/\+/g, " "));
      } catch (e) {}
      return raw
        .toString()
        .split(/\s*(?:\|\||\||,)\s*/)
        .map((p) => (p || "").trim())
        .filter(Boolean);
    };
    const tokens = decodeAndSplit(rawSm);
    if (!tokens.length) return;

    const selectors = [
      ".strmpr-search-result-filters .strmpr-dropdown .strmpr-dropdown-option",
      "#storemapper-filter-drop-down-1 .strmpr-dropdown-option",
      ".strmpr-dropdown-option",
    ];
    const options = Array.from(
      document.querySelectorAll(selectors.join(",")),
    ).filter(Boolean);

    if (!options.length) {
      if (retry < 6)
        setTimeout(() => this.processUrlParameters(retry + 1), 300);
      return;
    }

    const normalize = (txt) =>
      (txt || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

    let anyMatched = false;
    for (const token of tokens) {
      const tNorm = normalize(token);
      for (const opt of options) {
        const labelEl = opt.querySelector(
          "label, .strmpr-dropdown-option-label, .storemapper-category-link",
        );
        const labelText = (labelEl?.textContent || opt.textContent || "")
          .toString()
          .trim();
        const labelNorm = normalize(labelText);
        const checkbox = opt.querySelector(
          'input[type="checkbox"], input.storemapper-filter-checkbox',
        );
        const checkboxValue = normalize(checkbox?.value || "");
        const dataKey = normalize(
          opt.getAttribute("data-storemapper-key") ||
            opt.dataset?.storemapperKey ||
            "",
        );
        const dataOld = normalize(
          opt.getAttribute("data-storemapper-old") || "",
        );

        const matched =
          labelNorm === tNorm ||
          checkboxValue === tNorm ||
          dataKey === tNorm ||
          dataOld === tNorm ||
          labelNorm.includes(tNorm) ||
          dataKey.includes(tNorm) ||
          tNorm.includes(labelNorm);

        if (matched) {
          anyMatched = true;
          if (checkbox) {
            if (!checkbox.checked) {
              checkbox.checked = true;
              checkbox.dispatchEvent(new Event("change", { bubbles: true }));
            }
          } else if (labelEl) {
            labelEl.click();
          } else {
            opt.click();
          }
        }
      }
    }

    if (anyMatched) return;

    const select = document.getElementById("storemapper-collections");
    if (select && select.options.length) {
      for (const token of tokens) {
        const tNorm = normalize(token);
        const optMatch = Array.from(select.options).find((o) => {
          const txt = normalize(o.textContent);
          const val = normalize(o.value);
          const meta = normalize(
            o.dataset?.storemapperKey ||
              o.getAttribute("data-storemapper-key") ||
              "",
          );
          return (
            txt === tNorm ||
            val === tNorm ||
            meta === tNorm ||
            txt.includes(tNorm) ||
            meta.includes(tNorm) ||
            tNorm.includes(txt)
          );
        });
        if (optMatch) {
          select.value = optMatch.value;
          select.dispatchEvent(new Event("change", { bubbles: true }));
          return;
        }
      }
    }

    const resolvedName = this.resolveCollectionNameFromParam(rawSm);
    if (!resolvedName) {
      if (retry < 6)
        setTimeout(() => this.processUrlParameters(retry + 1), 300);
      return;
    }

    const resolvedNorm = normalize(resolvedName);
    for (const opt of options) {
      const labelEl = opt.querySelector(
        "label, .strmpr-dropdown-option-label, .storemapper-category-link",
      );
      const labelText = (labelEl?.textContent || opt.textContent || "")
        .toString()
        .trim();
      const labelNorm = normalize(labelText);
      const dataKey = normalize(
        opt.getAttribute("data-storemapper-key") ||
          opt.dataset?.storemapperKey ||
          "",
      );
      if (
        labelNorm === resolvedNorm ||
        dataKey === resolvedNorm ||
        labelNorm.includes(resolvedNorm) ||
        resolvedNorm.includes(labelNorm)
      ) {
        const checkbox = opt.querySelector(
          'input[type="checkbox"], input.storemapper-filter-checkbox',
        );
        if (checkbox) {
          if (!checkbox.checked) {
            checkbox.checked = true;
            checkbox.dispatchEvent(new Event("change", { bubbles: true }));
          }
        } else if (labelEl) {
          labelEl.click();
        } else {
          opt.click();
        }
        break;
      }
    }
  }

  /**
   * Arrange divs in proper structure
   */
  arrangeDivs() {
    this.movePageHeader();
    this.setupSearchButton();
  }

  /**
   * Move page header to search div
   */
  movePageHeader() {
    const { pageHeader } = this.elements;
    const searchDiv = this.storemapperDiv.querySelector(".strmpr-search");

    if (pageHeader && searchDiv) {
      pageHeader.classList.add("strmpr-header");
      searchDiv.insertBefore(pageHeader, searchDiv.firstChild);
    }
  }

  /**
   * Setup search button
   */
  setupSearchButton() {
    const { searchButton, filterBucket } = this.elements;

    if (searchButton && filterBucket) {
      searchButton.textContent = storemapperStrings.searchButton;
      filterBucket.appendChild(searchButton);
    }
  }

  /**
   * Add input labels
   */
  addInputLabels() {
    this.addShowMapToggle();
    this.addDistanceLabel();
    this.addSearchBoxLabel();
    this.addFiltersLabel();
  }

  /**
   * Add show map toggle
   */
  addShowMapToggle() {
    const { pageHeader } = this.elements;
    const searchDiv = this.storemapperDiv.querySelector(".strmpr-search");

    if (!searchDiv || !pageHeader) return;

    const showMapWrapper = this.createElement("div", "show-map-wrapper", [
      this.createElement("span", "show-map-label", [], "Show map"),
      this.createElement("button", "show-map-toggle", [
        this.createElement("span", "show-map-toggle-circle"),
      ]),
    ]);

    const toggleButton = showMapWrapper.querySelector(".show-map-toggle");
    toggleButton.setAttribute("aria-pressed", "true");

    toggleButton.addEventListener("click", () => this.toggleMap(toggleButton));

    pageHeader.insertAdjacentElement("afterend", showMapWrapper);
  }

  /**
   * Toggle map visibility
   */
  toggleMap(toggleButton) {
    const { mapContainer } = this.elements;
    if (!mapContainer) return;

    const isHidden = mapContainer.style.display === "none";
    mapContainer.style.display = isHidden ? "" : "none";
    toggleButton.setAttribute("aria-pressed", isHidden.toString());
  }

  /**
   * Add distance label
   */
  addDistanceLabel() {
    const distanceDiv = this.storemapperDiv.querySelector(
      ".storemapper-distance .strmpr-distance-btn",
    );
    if (distanceDiv) {
      const label = this.createElement(
        "p",
        ["label-distance", "storemapper-label"],
        [],
        storemapperStrings.within,
      );
      distanceDiv.parentNode.insertBefore(label, distanceDiv);
    }
  }

  /**
   * Add search box label
   */
  addSearchBoxLabel() {
    const searchBoxDiv = this.storemapperDiv.querySelector(
      ".strmpr-search-box-wrapper .strmpr-search-box",
    );
    if (searchBoxDiv) {
      const label = this.createElement(
        "p",
        ["label-search-box", "storemapper-label"],
        [],
        storemapperStrings.zipCode,
      );
      searchBoxDiv.parentNode.insertBefore(label, searchBoxDiv);
    }
  }

  /**
   * Add filters label
   */
  addFiltersLabel() {
    const { filterBucket } = this.elements;
    if (!filterBucket) return;

    const labelText =
      this.pageType === "designer-event"
        ? storemapperStrings.searchDesign
        : storemapperStrings.searchRetailer;
    const label = this.createElement(
      "p",
      ["label-search-result-filters", "storemapper-label"],
      [],
      labelText,
    );
    filterBucket.parentNode.insertBefore(label, filterBucket);
  }

  /**
   * Setup Search Results Observer
   */
  setupSearchResultsObserver() {
    const storemapperList = document.getElementById("storemapper-list");
    if (!storemapperList) return;

    if (this.observers.has("searchResults")) {
      this.observers.get("searchResults").disconnect();
    }

    const observer = new MutationObserver((mutations) => {
      let hasNewResults = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          const newResults = Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              (node.classList?.contains("strmpr-search-result") ||
                node.querySelector?.(".strmpr-search-result")),
          );
          if (newResults) {
            hasNewResults = true;
          }
        }
      });

      if (hasNewResults) {
        if (this.shouldApplyStrictCountryOnlyFilter()) {
          this.applyStrictCountryOnlyFilter();
        }
        setTimeout(() => {
          this.handleCustomFunctionalities();
        }, 100);
      }
    });

    observer.observe(storemapperList, {
      childList: true,
      subtree: true,
    });

    this.observers.set("searchResults", observer);
  }

  /**
   * Handle custom functionalities
   */
  handleCustomFunctionalities() {
    clearTimeout(this.functionalitiesTimeout);
    this.functionalitiesTimeout = setTimeout(() => {
      this.customFunctionalities();
    }, 50);
  }

  /**
   * Custom functionalities after search
   */
  customFunctionalities() {
    const searchResults = document.querySelectorAll(
      ".strmpr-search-result[data-idx]",
    );
    if (searchResults.length === 0) {
      setTimeout(() => {
        const retryResults = document.querySelectorAll(
          ".strmpr-search-result[data-idx]",
        );
        if (retryResults.length > 0) {
          this.performCustomFunctionalities();
          return;
        }
      }, 100);
      return;
    }

    this.performCustomFunctionalities();
  }

  /**
   * Perfrom Custom functionalities after search
   */
  performCustomFunctionalities() {
    this.addFilterResultsHeader();
    this.handleResultsVisibility();
    this.processSearchResults();
    this.applyStrictCountryOnlyFilter();
    this.reorderResultsPrioritizeRequestBooking();
    this.applyActiveFacetFilters();
    this.setupImageModal();
    this.handleMapDesign();
  }

  /**
   * Add filter results header
   */
  addFilterResultsHeader() {
    const { filterBucket } = this.elements;
    if (!filterBucket || filterBucket.querySelector(".strmpr-header-result"))
      return;

    const headerText =
      this.pageType === "find-retailer"
        ? storemapperStrings.retailerResult
        : storemapperStrings.eventResult;
    const header = this.createElement(
      "h4",
      ["strmpr-header", "strmpr-header-result"],
      [],
      headerText,
    );
    filterBucket.appendChild(header);
  }

  /**
   * Handle results visibility for find-retailer page
   */
  handleResultsVisibility() {
    if (this.pageType !== "find-retailer") return;

    const { storemapperResults, resetButton } = this.elements;
    const mapPins = document.querySelectorAll("gmp-advanced-marker");

    if (resetButton && !this.resetVisibilityBound) {
      resetButton.addEventListener("click", () => {
        storemapperResults?.classList.add("hidden");
        mapPins.forEach((pin) => pin.classList.add("hidden"));
      });
      this.resetVisibilityBound = true;
    }

    const validation = this.validateSearchInputs();

    // Show results only after an explicitly executed valid search.
    if (this.hasExecutedSearch && validation.isValid) {
      storemapperResults?.classList.remove("hidden");
      mapPins.forEach((pin) => pin.classList.remove("hidden"));
      return;
    }

    storemapperResults?.classList.add("hidden");
    mapPins.forEach((pin) => pin.classList.add("hidden"));
  }

  /**
   * Process all search results efficiently in a single loop
   */
  processSearchResults() {
    const searchResults = document.querySelectorAll(
      ".strmpr-search-result[data-idx]",
    );
    if (searchResults.length === 0) return;

    // Process all search result related functionality in one loop
    searchResults.forEach((resultDiv, index) => {
      this.processIndividualResult(resultDiv, index);
    });

    // Handle elements that need to be processed separately
    this.processViewMoreButton();
    this.addResultDividers();
    this.processEventDates();
    this.processEventTypes();
  }

  /**
   * Process individual search result
   */
  processIndividualResult(resultDiv, index) {
    const idx = resultDiv.getAttribute("data-idx");

    // Setup map interaction
    this.setupMapInteraction(resultDiv, idx);

    // Add store number
    this.addStoreNumber(resultDiv, index);

    // Wrap store info elements
    this.wrapStoreInfo(resultDiv);

    // Handle featured retailers
    this.handleFeaturedRetailer(resultDiv);

    // Process booking buttons
    this.processBookingButtons(resultDiv);

    // Setup image modal for logo
    this.setupLogoImageModal(resultDiv);

    // Add custom div below request booking button
    this.addDivBelowRequestBooking(resultDiv);
  }

  /**
   * Setup map interaction for result
   */
  setupMapInteraction(resultDiv, idx) {
    const mapDiv = document.querySelector(
      `.storemapper_view[data-idx="${idx}"]`,
    );
    if (!mapDiv) return;

    // Remove any existing event listeners
    const existingHandler = resultDiv._mapInteractionHandler;
    if (existingHandler) {
      resultDiv.removeEventListener("mouseenter", existingHandler);
      resultDiv.removeEventListener("click", existingHandler);
    }

    // Create a new handler
    const mapInteractionHandler = (event) => {
      if (
        event.target.tagName === "BUTTON" ||
        event.target.tagName === "A" ||
        event.target.closest("button") ||
        event.target.closest("a") ||
        event.target.hasAttribute("onclick")
      ) {
        return;
      }

      if (event.type === "mouseenter" && "ontouchstart" in window) {
        return;
      }

      mapDiv.click();
    };

    // Store the handler reference for cleanup
    resultDiv._mapInteractionHandler = mapInteractionHandler;

    // Add event listeners
    resultDiv.addEventListener("mouseenter", mapInteractionHandler);
    resultDiv.addEventListener("click", mapInteractionHandler);
  }

  /**
   * Add store number to result
   */
  addStoreNumber(resultDiv, index) {
    const nameDiv = resultDiv.querySelector(".strmpr-field-name");
    if (!nameDiv || nameDiv.querySelector(".store-number")) return;

    const numberSpan = this.createElement(
      "span",
      "store-number",
      [],
      `${index + 1} `,
    );
    nameDiv.prepend(numberSpan);
  }

  /**
   * Wrap store info elements
   */
  wrapStoreInfo(resultDiv) {
    const bucket = resultDiv.querySelector(".strmpr-field-bucket-general");
    if (!bucket) return;

    const directions = bucket.querySelector(
      ".strmpr-field-directions.storemapper_directions",
    );
    const url = bucket.querySelector(".strmpr-field-url");

    if (
      directions &&
      url &&
      !directions.parentElement.classList.contains("strmpr-store__info")
    ) {
      const wrapper = this.createElement("div", "strmpr-store__info");
      bucket.insertBefore(wrapper, directions);
      wrapper.appendChild(directions);
      wrapper.appendChild(url);
    }
  }

  /**
   * Handle featured retailer styling
   */
  handleFeaturedRetailer(resultDiv) {
    const featured = resultDiv.querySelector(
      ".strmpr-field-custom.featured_retailer",
    );
    if (!featured) return;

    featured.classList.remove("hidden");

    const featuredText = featured.textContent.trim();
    if (
      featuredText.match(/Featured:\s*Y/i) ||
      featuredText === storemapperStrings.featured
    ) {
      featured.textContent = storemapperStrings.featured;
    } else {
      featured.classList.add("hidden");
    }
  }

  /**
   * Process booking buttons
   */
  processBookingButtons(resultDiv) {
    this.processBookAppointmentButtons(resultDiv);
    this.processRequestAppointmentButtons(resultDiv);
  }

  /**
   * Process book appointment buttons
   */
  processBookAppointmentButtons(resultDiv) {
    const bookButtons = resultDiv.querySelectorAll(
      ".strmpr-field-custom.direct_booking button",
    );

    bookButtons.forEach((button) => {
      if (button.dataset.parameterized) return;

      const retailerIdField = resultDiv.querySelector(
        ".strmpr-field-custom.retailer_id",
      );
      const bridalRetailIdField = resultDiv.querySelector(
        ".strmpr-field-custom.bridal_retailid",
      );
      const retailerNameField = resultDiv.querySelector(
        ".strmpr-field-bucket-general .strmpr-field-name",
      );

      const retailerId = this.extractFieldValue(retailerIdField);
      const bridalRetailId = this.extractFieldValue(bridalRetailIdField);
      const retailerName = retailerNameField
        ? this.extractRetailerName(retailerNameField)
        : "";

      if (retailerId || bridalRetailId) {
        this.updateButtonOnclick(button, {
          retailerId,
          bridalretailid: bridalRetailId,
          retailerName: retailerName,
        });
        button.dataset.parameterized = "true";
      }
    });
  }

  /**
   * Process request appointment buttons
   */
  processRequestAppointmentButtons(resultDiv) {
    try {
      const reqBookingDiv = resultDiv.querySelector(
        ".strmpr-field-custom.req_booking",
      );
      if (!reqBookingDiv) return;

      const requestButtons = reqBookingDiv.querySelectorAll("button");
      requestButtons.forEach((button) => {
        if (button.dataset.parameterized) return;
        const retailerIdField = resultDiv.querySelector(
          ".strmpr-field-custom.retailer_id",
        );
        const emailField = resultDiv.querySelector(
          ".strmpr-field-bucket-general .strmpr-field-email a",
        );
        const retailerNameField = resultDiv.querySelector(
          ".strmpr-field-bucket-general .strmpr-field-name",
        );
        const retailerId = this.extractFieldValue(retailerIdField);
        const emailHref = emailField?.getAttribute("href") || "";
        const email = emailHref.replace(/^mailto:/i, "").trim();
        const retailerName = retailerNameField
          ? this.extractRetailerName(retailerNameField)
          : "";
        if (retailerId) {
          this.updateButtonOnclick(button, {
            id: retailerId,
            email: email,
            retailerName: retailerName,
          });
          button.dataset.parameterized = "true";
        }
      });
    } catch (err) {
      console.error("Error processing request buttons:", err);
    }
  }

  MOBILE_BREAKPOINT = 960;
  MIN_DRAWER_HEIGHT = 100;
  MAX_DRAWER_HEIGHT_RATIO = 0.9;
  PANEL_ID = "strmpr-event-detail-panel";

  formatDateCanonical(date) {
    if (!date || !(date instanceof Date) || isNaN(date)) return "";
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  parseDateFromRawText(rawText) {
    if (!rawText) return { dateStr: "", date: null };

    let s = rawText
      .toString()
      .replace(/\u00A0/g, " ")
      .trim();
    s = s.replace(/^[^:]*:\s*/, "").trim(); // drop labels before colon
    s = s.replace(/(\d)(st|nd|rd|th)/gi, "$1"); // remove ordinals
    s = s.replace(/\s+/g, " ").trim();

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthAlt = monthNames
      .concat(monthNames.map((m) => m.slice(0, 3)))
      .map((m) => m.replace(".", ""))
      .join("|");
    const monthMap = monthNames.reduce((acc, m, i) => {
      acc[m.toLowerCase()] = i;
      acc[m.slice(0, 3).toLowerCase()] = i;
      return acc;
    }, {});

    // 1) MM/DD/YYYY or M-D-YYYY or M.D.YYYY
    let m = s.match(/^\s*(\d{1,2})[\/\-\.\s](\d{1,2})[\/\-\.\s](\d{4})\s*$/);
    if (m) {
      const month = parseInt(m[1], 10) - 1;
      const day = parseInt(m[2], 10);
      const year = parseInt(m[3], 10);
      const dt = new Date(year, month, day);
      if (!isNaN(dt))
        return { dateStr: this.formatDateCanonical(dt), date: dt };
    }

    // 2) YYYY-MM-DD or YYYY/MM/DD
    m = s.match(/^\s*(\d{4})[\/\-\.\s](\d{1,2})[\/\-\.\s](\d{1,2})\s*$/);
    if (m) {
      const year = parseInt(m[1], 10);
      const month = parseInt(m[2], 10) - 1;
      const day = parseInt(m[3], 10);
      const dt = new Date(year, month, day);
      if (!isNaN(dt))
        return { dateStr: this.formatDateCanonical(dt), date: dt };
    }

    // 3) "MonthName D, YYYY" or "Mon D YYYY"
    let mn = s.match(
      new RegExp(
        `^\\s*(${monthAlt})\\.?\\s+(\\d{1,2}),?\\s*(\\d{4})\\s*$`,
        "i",
      ),
    );
    if (mn) {
      const monthText = mn[1].replace(".", "").trim().toLowerCase();
      const day = parseInt(mn[2], 10);
      const year = parseInt(mn[3], 10);
      const monthIndex = monthMap[monthText];
      if (monthIndex !== undefined) {
        const dt = new Date(year, monthIndex, day);
        if (!isNaN(dt))
          return { dateStr: this.formatDateCanonical(dt), date: dt };
      }
    }

    // 4) "D MonthName YYYY"
    let dm = s.match(
      new RegExp(
        `^\\s*(\\d{1,2})\\s+(${monthAlt})\\.?\\,?\\s*(\\d{4})\\s*$`,
        "i",
      ),
    );
    if (dm) {
      const day = parseInt(dm[1], 10);
      const monthText = dm[2].replace(".", "").trim().toLowerCase();
      const year = parseInt(dm[3], 10);
      const monthIndex = monthMap[monthText];
      if (monthIndex !== undefined) {
        const dt = new Date(year, monthIndex, day);
        if (!isNaN(dt))
          return { dateStr: this.formatDateCanonical(dt), date: dt };
      }
    }

    // 5) Partial mm/dd or "Month D" without year -> assume current year
    let partial = s.match(/^\s*(\d{1,2})[\/\-\.\s](\d{1,2})\s*$/);
    if (partial) {
      const month = parseInt(partial[1], 10);
      const day = parseInt(partial[2], 10);
      const year = new Date().getFullYear();
      if (month >= 1 && month <= 12) {
        const dt = new Date(year, month - 1, day);
        if (!isNaN(dt))
          return { dateStr: this.formatDateCanonical(dt), date: dt };
      }
    }

    let mnPartial = s.match(
      new RegExp(`^\\s*(${monthAlt})\\.?\\s+(\\d{1,2})\\s*$`, "i"),
    );
    if (mnPartial) {
      const monthText = mnPartial[1].replace(".", "").trim().toLowerCase();
      const day = parseInt(mnPartial[2], 10);
      const monthIndex = monthMap[monthText];
      const year = new Date().getFullYear();
      if (monthIndex !== undefined) {
        const dt = new Date(year, monthIndex, day);
        if (!isNaN(dt))
          return { dateStr: this.formatDateCanonical(dt), date: dt };
      }
    }

    // 6) Safe fallback: try Date.parse only if a 4-digit year exists or contains letters
    if (/\b\d{4}\b/.test(s) || /[a-zA-Z]/.test(s)) {
      const parsed = new Date(s);
      if (!isNaN(parsed))
        return { dateStr: this.formatDateCanonical(parsed), date: parsed };
    }

    return { dateStr: "", date: null };
  }

  //Helper Method: Resolve logo source
  resolveLogoSrc(logoImg, publicImageField) {
    if (logoImg?.src) return logoImg.src;
    if (!publicImageField) return "";

    const fullText = publicImageField.textContent.trim();
    const colonIndex = fullText.indexOf(":");
    const raw =
      colonIndex !== -1
        ? fullText.substring(colonIndex + 1).trim()
        : (fullText.match(/https?:\/\/[^\s]+/)?.[0] ?? "");

    return raw.replace(/["']/g, "").trim();
  }

  addDivBelowRequestBooking(resultDiv) {
    const directBookingDiv = resultDiv.querySelector(
      ".strmpr-field-custom.direct_booking",
    );
    if (directBookingDiv) {
      const buttonText = directBookingDiv
        .querySelector("button")
        ?.textContent?.trim();
      if (!buttonText) {
        directBookingDiv.style.setProperty("display", "none");
      }
    }

    if (this.pageType !== "find-retailer") return;

    try {
      const reqBookingDiv = resultDiv.querySelector(
        ".strmpr-field-custom.req_booking",
      );
      if (!reqBookingDiv) return;

      if (
        reqBookingDiv.nextElementSibling?.classList.contains(
          "strmpr-upcoming-event",
        )
      )
        return;
      const startDateField = resultDiv.querySelector(
        ".strmpr-field-custom.event_start-date",
      );
      const endDateField = resultDiv.querySelector(
        ".strmpr-field-custom.event_end-date",
      );
      const startRaw = startDateField?.textContent?.trim() ?? "";
      const endRaw = endDateField?.textContent?.trim() ?? "";
      if (!startRaw) return;
      const rawStartText = startRaw;
      const rawEndText = endRaw;
      let startParsed = this.parseDateFromRawText(startRaw);
      if (!startParsed.date) {
        try {
          const cleanedStart = startRaw
            .replace(/\s*,\s*/, ", ")
            .replace(/\u00A0/g, " ")
            .trim();
          const parsedDirect = new Date(cleanedStart);
          if (!isNaN(parsedDirect)) {
            startParsed = {
              dateStr: this.formatDateCanonical(parsedDirect),
              date: parsedDirect,
            };
          }
        } catch (e) {
          /* ... */
        }
      }
      if (!startParsed.date) return;
      const eventDate = startParsed.date;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (eventDate < today) return;
      let endParsed = this.parseDateFromRawText(endRaw);
      if (!endParsed.date && endRaw) {
        try {
          const cleanedEnd = endRaw
            .replace(/\s*,\s*/, ", ")
            .replace(/\u00A0/g, " ")
            .trim();
          const parsedDirectEnd = new Date(cleanedEnd);
          if (!isNaN(parsedDirectEnd)) {
            endParsed = {
              dateStr: this.formatDateCanonical(parsedDirectEnd),
              date: parsedDirectEnd,
            };
          }
        } catch (e) {
          /* ... */
        }
      }
      let eventDateText = "";
      const fmt = this.formatRetailerEventDateRange(rawStartText, rawEndText);
      if (fmt) {
        eventDateText = fmt;
      } else if (startParsed.date) {
        eventDateText = this.formatDateCanonical(startParsed.date);
      } else {
        eventDateText = rawStartText || "";
      }
      const upcomingEventDiv = this.createElement(
        "button",
        "strmpr-upcoming-event",
        [],
        `
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" class="calendar-icon">
          <path d="M18 1.62H14.08V0H13.16V1.62H4.85V0H3.93V1.62H0V18H18V1.62ZM17.08 17.11H0.92V6.33H17.07V17.1L17.08 17.11ZM17.08 5.44H0.92V2.52H3.92V4.09H4.84V2.52H13.15V4.09H14.07V2.52H17.07V5.44H17.08Z" fill="currentColor"></path>
          <path d="M3 11.27H4.62V12.17H3V11.27Z" fill="currentColor"></path>
          <path d="M3 13.96H4.62V14.86H3V13.96Z" fill="currentColor"></path>
          <path d="M6.46002 8.56995H8.08002V9.46995H6.46002V8.56995Z" fill="currentColor"></path>
          <path d="M6.46002 11.27H8.08002V12.17H6.46002V11.27Z" fill="currentColor"></path>
          <path d="M6.46002 13.96H8.08002V14.86H6.46002V13.96Z" fill="currentColor"></path>
          <path d="M9.91998 8.56995H11.54V9.46995H9.91998V8.56995Z" fill="currentColor"></path>
          <path d="M9.91998 11.27H11.54V12.17H9.91998V11.27Z" fill="currentColor"></path>
          <path d="M9.91998 13.96H11.54V14.86H9.91998V13.96Z" fill="currentColor"></path>
          <path d="M13.38 8.56995H15V9.46995H13.38V8.56995Z" fill="currentColor"></path>
          <path d="M13.38 11.27H15V12.17H13.38V11.27Z" fill="currentColor"></path>
        </svg>
        <div class="upcoming-event-content">
          <span class="upcoming-event-title">Upcoming Event</span>
          <p class="upcoming-event-date">${eventDateText}</p>
        </div>
        <div class="svg-wrapper">
          <svg class="icon icon-arrow" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <g id="Icons/Directional-Navigation/Chevron-Right">
              <path id="Vector" d="M9.5 17L14.5 12L9.5 7" stroke="#58514C" stroke-linecap="round" stroke-linejoin="round"></path>
            </g>
          </svg>
        </div>
      `,
      );
      upcomingEventDiv.addEventListener("click", (e) => {
        e.stopPropagation();
        this.openEventDetailPanel(resultDiv);
      });
      reqBookingDiv.insertAdjacentElement("afterend", upcomingEventDiv);
    } catch (err) {
      console.error("Error setting up upcoming event section:", err);
    }
  }

  setupEventDetailPanel() {
    const mapContainer = document.querySelector(
      ".strmpr-layout-horizontal .strmpr-map",
    );
    if (!mapContainer) return;

    let panel = document.getElementById(this.PANEL_ID);
    if (!panel) {
      panel = this.createElement(
        "div",
        "strmpr-event-detail-panel",
        [],
        `
        <div class="strmpr-panel-overlay"></div>
        <div class="strmpr-panel-resizer">
          <div class="strmpr-panel-resizer-line">&nbsp;</div>
        </div>
        <button class="strmpr-panel-close" aria-label="Close panel">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="strmpr-panel-content">
          <div class="strmpr-panel-body"></div>
        </div>
      `,
      );
      panel.id = this.PANEL_ID;

      this.attachPanelToCorrectParent(panel);

      panel
        .querySelector(".strmpr-panel-overlay")
        .addEventListener("click", () => this.closeEventDetailPanel());

      panel
        .querySelector(".strmpr-panel-close")
        .addEventListener("click", () => this.closeEventDetailPanel());

      this.setupDrawerResizer(panel);

      let resizeTimer;
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(
          () => this.attachPanelToCorrectParent(panel),
          150,
        );
      });
    }
  }

  attachPanelToCorrectParent(panel) {
    const mapContainer = document.querySelector(
      ".strmpr-layout-horizontal .strmpr-map",
    );
    const isMobile = window.innerWidth <= this.MOBILE_BREAKPOINT;

    if (isMobile) {
      if (panel.parentElement !== document.body) {
        document.body.appendChild(panel);
        panel.classList.add("strmpr-drawer-mode");
        panel.classList.remove("strmpr-panel-mode");
      }
    } else {
      if (mapContainer && panel.parentElement !== mapContainer) {
        mapContainer.appendChild(panel);
        panel.classList.add("strmpr-panel-mode");
        panel.classList.remove("strmpr-drawer-mode");
      }
    }
  }

  setupDrawerResizer(panel) {
    const resizer = panel.querySelector(".strmpr-panel-resizer");
    if (!resizer) return;

    let startY = 0;
    let startHeight = 0;
    let isDragging = false;

    const onStart = (e) => {
      if (!panel.classList.contains("strmpr-drawer-mode")) return;
      isDragging = true;
      startY = e.touches ? e.touches[0].clientY : e.clientY;
      startHeight = panel.offsetHeight;
      document.addEventListener("mousemove", onMove);
      document.addEventListener("touchmove", onMove, { passive: false });
      document.addEventListener("mouseup", onEnd);
      document.addEventListener("touchend", onEnd);
    };

    const onMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const currentY = e.touches ? e.touches[0].clientY : e.clientY;
      const diff = startY - currentY;
      const newHeight = Math.min(
        Math.max(startHeight + diff, this.MIN_DRAWER_HEIGHT),
        window.innerHeight * this.MAX_DRAWER_HEIGHT_RATIO,
      );
      panel.style.height = `${newHeight}px`;
    };

    const onEnd = () => {
      isDragging = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchend", onEnd);
    };

    resizer.addEventListener("mousedown", onStart);
    resizer.addEventListener("touchstart", onStart, { passive: true });
  }

  openEventDetailPanel(resultDiv) {
    this.setupEventDetailPanel();

    const panel = document.getElementById(this.PANEL_ID);
    const panelBody = panel?.querySelector(".strmpr-panel-body");
    if (!panelBody) return;

    const nameField = resultDiv.querySelector(".strmpr-field-name");
    const logoImg = resultDiv.querySelector(".strmpr-field-logo img");
    const publicImageField = resultDiv.querySelector(
      ".strmpr-field-custom.public_image_url",
    );
    const startDateField = resultDiv.querySelector(
      ".strmpr-field-custom.event_start-date",
    );
    const endDateField = resultDiv.querySelector(
      ".strmpr-field-custom.event_end-date",
    );
    const collectionField = resultDiv.querySelector(
      ".strmpr-field-custom.strmpr_collection--list",
    );
    const descriptionField = resultDiv.querySelector(
      ".strmpr-field-description",
    );

    const retailerName = nameField ? this.extractRetailerName(nameField) : "";
    const logoSrc = this.resolveLogoSrc(logoImg, publicImageField);
    const collectionText = collectionField
      ? collectionField.textContent.replace("Collection:", "").trim()
      : "";
    const descriptionText = descriptionField
      ? descriptionField.textContent.trim()
      : "";

    let eventDateText = "";
    if (startDateField && endDateField) {
      const stripLabel = (txt) =>
        (txt || "")
          .toString()
          .replace(/^[^:]*:\s*/, "")
          .replace(/\u00A0/g, " ")
          .trim();

      const rawStartText = stripLabel(startDateField.textContent);
      const rawEndText = stripLabel(endDateField.textContent);

      const splitRange = (text) => {
        if (!text) return [text];
        const parts = text
          .split(/\s*[–—-]\s*/)
          .map((p) => p.trim())
          .filter(Boolean);
        return parts.length > 1 ? parts : [text];
      };

      const startCandidates = splitRange(rawStartText);
      const endCandidates = splitRange(rawEndText);

      let startRaw = startCandidates[0] || "";
      let endRaw =
        endCandidates.length > 1
          ? endCandidates[endCandidates.length - 1]
          : startCandidates.length > 1
            ? startCandidates[startCandidates.length - 1]
            : rawEndText || "";

      let startParsed = this.parseDateFromRawText(startRaw);
      let endParsed = this.parseDateFromRawText(endRaw);

      if (!startParsed.date && endParsed.date) {
        try {
          const candidate = `${startRaw}, ${endParsed.date.getFullYear()}`;
          const parsedDirect = new Date(candidate);
          if (!isNaN(parsedDirect)) {
            startParsed = {
              dateStr: this.formatDateCanonical(parsedDirect),
              date: parsedDirect,
            };
          }
        } catch (e) {
          /* ignore */
        }
      }

      if (!endParsed.date && startParsed.date) {
        try {
          const candidate = `${endRaw}, ${startParsed.date.getFullYear()}`;
          const parsedDirect = new Date(candidate);
          if (!isNaN(parsedDirect)) {
            endParsed = {
              dateStr: this.formatDateCanonical(parsedDirect),
              date: parsedDirect,
            };
          }
        } catch (e) {
          /* ignore */
        }
      }

      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      if (startParsed.date && endParsed.date) {
        const s = startParsed.date,
          e = endParsed.date;
        if (s.getFullYear() === e.getFullYear()) {
          eventDateText = `${monthNames[s.getMonth()]} ${s.getDate()} - ${monthNames[e.getMonth()]} ${e.getDate()}, ${s.getFullYear()}`;
        } else {
          eventDateText = `${monthNames[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()} - ${monthNames[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
        }
      } else if (startParsed.date) {
        const s = startParsed.date;
        eventDateText = `${monthNames[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()}`;
      } else if (endParsed.date) {
        const e = endParsed.date;
        if (/[A-Za-z]/.test(startRaw)) {
          eventDateText = `${startRaw} - ${monthNames[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
        } else {
          eventDateText = `${monthNames[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
        }
      } else {
        const fmt = this.formatRetailerEventDateRange(startRaw, endRaw);
        eventDateText = fmt || startRaw || "";
      }
      eventDateText = (eventDateText || "")
        .replace(/,\s*,+/g, ",")
        .replace(/,\s*$/, "")
        .trim();
    }
    panelBody.innerHTML = `
      ${
        logoSrc
          ? `<div class="strmpr-panel-image">
        <img src="${logoSrc}" alt="${retailerName}" loading="lazy" onerror="this.parentElement.style.display='none'">
      </div>`
          : ""
      }
      <div class="strmpr-panel-info">
        ${retailerName ? `<h3 class="strmpr-panel-retailer">${retailerName}</h3>` : ""}
        ${eventDateText ? `<p class="strmpr-panel-dates">${eventDateText}</p>` : ""}
        ${
          collectionText
            ? `<div class="strmpr-panel-collection-section">
          <p class="strmpr-panel-collection-title">Collections</p>
          <p class="strmpr-panel-collection">${collectionText}</p>
        </div>`
            : ""
        }
        ${descriptionText ? `<p class="strmpr-panel-description">${descriptionText}</p>` : ""}
      </div>
    `;

    this.attachPanelToCorrectParent(panel);

    if (window.innerWidth <= this.MOBILE_BREAKPOINT) {
      panel.style.height = "65%";
    }

    panel.classList.add("active");
  }

  closeEventDetailPanel() {
    const panel = document.getElementById(this.PANEL_ID);
    if (!panel) return;
    panel.classList.remove("active");
    setTimeout(() => {
      panel.style.height = "";
    }, 300);
  }
  /**
   * Extract retailer name excluding store number
   */
  extractRetailerName(nameField) {
    // Clone the element to avoid modifying the original
    const tempElement = nameField.cloneNode(true);

    // Remove the store number span
    const storeNumberSpan = tempElement.querySelector(".store-number");
    if (storeNumberSpan) {
      storeNumberSpan.remove();
    }

    let retailerName = tempElement.textContent.trim();
    retailerName = retailerName.replace(/\s+/g, " ").trim();

    return retailerName;
  }

  /**
   * Custom URL encoding that handles apostrophes properly
   */
  customEncodeURIComponent(str) {
    let encoded = encodeURIComponent(str.toString().trim());
    encoded = encoded.replace(/'/g, "%27");
    return encoded;
  }

  /**
   * Update button onclick with parameters
   */
  updateButtonOnclick(button, params) {
    const originalOnclick = button.getAttribute("onclick");
    const urlMatch = originalOnclick?.match(/window\.open\(['"]([^'"]+)['"]/);

    if (!urlMatch) return;

    let baseUrl = urlMatch[1];

    const allParams = { ...params, ...this.utmParams };
    const paramStrings = Object.entries(allParams)
      .filter(([, value]) => value && value.toString().trim() !== "")
      .map(([key, value]) => {
        const encodedValue = this.customEncodeURIComponent(value);
        return `${key}=${encodedValue}`;
      });

    if (paramStrings.length === 0) return;

    const separator = baseUrl.includes("?") ? "&" : "?";
    const newUrl = baseUrl + separator + paramStrings.join("&");

    button.setAttribute("onclick", `window.location.href='${newUrl}'`);
  }

  /**
   * Extract field value from custom field
   */
  extractFieldValue(field) {
    return field ? field.textContent.trim().split(": ")[1] || "" : "";
  }

  /**
   * Setup logo image modal
   */
  setupLogoImageModal(resultDiv) {
    const logoImg = resultDiv.querySelector(".strmpr-field-logo img");
    if (!logoImg || this.processedElements.has(logoImg)) return;

    logoImg.style.cursor = "pointer";
    logoImg.addEventListener("click", (event) => {
      event.stopPropagation();
      this.showImageModal(logoImg.src);
    });

    this.processedElements.add(logoImg);
  }

  /**
   * Process view more button
   */
  processViewMoreButton() {
    const viewMoreButton = document.querySelector(
      ".strmpr-view-more-stores-button",
    );
    viewMoreButton?.classList.add("button");
  }

  /**
   * Add dividers between results
   */
  addResultDividers() {
    // Remove existing dividers
    document
      .querySelectorAll(".strmpr-divider")
      .forEach((divider) => divider.remove());

    const items = Array.from(
      document.querySelectorAll("#storemapper-list .strmpr-search-result"),
    ).filter((item) => {
      if (!(item instanceof HTMLElement)) return false;
      if (item.classList.contains("strmpr-country-filter-hidden")) return false;
      return item.style.display !== "none";
    });
    items.forEach((item, index) => {
      if (index !== items.length - 1) {
        const divider = this.createElement("div", "strmpr-divider", [], " ");
        item.parentNode.insertBefore(divider, item.nextSibling);
      }
    });
  }

  reorderResultsPrioritizeRequestBooking() {
    if (this.pageType !== "find-retailer") return;

    const list = document.getElementById("storemapper-list");
    if (!list) return;

    list.querySelectorAll(".strmpr-divider").forEach((d) => d.remove());

    const items = Array.from(
      list.querySelectorAll(":scope > .strmpr-search-result[data-idx]"),
    );
    if (!items.length) return;

    const visibleItems = items.filter(
      (item) =>
        !item.classList.contains("strmpr-country-filter-hidden") &&
        item.style.display !== "none",
    );
    const hiddenItems = items.filter((item) => !visibleItems.includes(item));

    const prioritized = [];
    const rest = [];

    visibleItems.forEach((item) => {
      const reqBooking = item.querySelector(".strmpr-field-custom.req_booking");
      const isActive =
        reqBooking && !reqBooking.classList.contains("strmpr-hidden");
      if (isActive) {
        prioritized.push(item);
      } else {
        rest.push(item);
      }
    });

    const orderedVisible = prioritized.length ? prioritized.concat(rest) : rest;
    orderedVisible
      .concat(hiddenItems)
      .forEach((node) => list.appendChild(node));

    this.addResultDividers();

    orderedVisible.forEach((el, i) => {
      const storeNum = el.querySelector(".store-number");
      if (storeNum) {
        storeNum.textContent = `${i + 1} `;
      }
    });
  }

  /**
   * Process event dates
   */
  processEventDates() {
    const eventStartDate = document.querySelectorAll(
      ".strmpr-search-result .strmpr-field-custom.event_start-date",
    );
    const eventEndDate = document.querySelectorAll(
      ".strmpr-search-result .strmpr-field-custom.event_end-date",
    );

    eventStartDate.forEach((startElement, index) => {
      const endElement = eventEndDate[index];
      if (!endElement) return;

      const startText = startElement.textContent || "";
      const endText = endElement.textContent || "";

      const formattedDate = this.formatRetailerEventDateRange(
        startText,
        endText,
      );
      if (formattedDate) {
        startElement.textContent = formattedDate;
      }
    });
  }

  formatRetailerEventDateRange(startText, endText = "") {
    const startParsed = this.parseDateFromRawText(startText);
    if (!startParsed.date) return null;

    const endParsed = endText
      ? this.parseDateFromRawText(endText)
      : { date: null };

    const s = startParsed.date;
    const e = endParsed.date || null;

    if (!e) {
      return this.formatDateCanonical(s);
    }

    if (s.getFullYear() === e.getFullYear()) {
      const startMonth = s.toLocaleString("default", { month: "long" });
      const endMonth = e.toLocaleString("default", { month: "long" });
      const year = s.getFullYear();
      return `${startMonth} ${s.getDate()} - ${endMonth} ${e.getDate()}, ${year}`;
    }
    return `${this.formatDateCanonical(s)} - ${this.formatDateCanonical(e)}`;
  }
  /**
   * Process event types
   */
  processEventTypes() {
    const eventTypes = document.querySelectorAll(
      ".strmpr-search-result .strmpr-field-custom.strmpr_event-type",
    );

    eventTypes.forEach((element) => {
      const text = element.textContent.trim();
      const colonIndex = text.indexOf(":");

      if (colonIndex !== -1) {
        const valueAfterColon = text.substring(colonIndex + 1).trim();
        element.textContent = valueAfterColon;
      }
    });
  }

  /**
   * Setup image modal
   */
  setupImageModal() {
    let imageModal = document.getElementById("strmpr-image-modal");
    if (imageModal) return;

    imageModal = this.createElement(
      "div",
      "strmpr-image-modal",
      [],
      `
      <div class="strmpr-modal-content">
        <div class="strmpr-modal-inner">
          <span class="strmpr-modal-close">
            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="14.9194" y="4.99121" width="0.833333" height="13.3333" transform="rotate(45 14.9194 4.99121)" fill="#58514C"/>
              <path d="M15.5087 14.4194L14.9194 15.0087L5.49132 5.5806L6.08058 4.99134L15.5087 14.4194Z" fill="#58514C"/>
            </svg>
          </span>
          <img class="strmpr-modal-img" src="" alt="Store Image">
        </div>
      </div>
    `,
    );

    imageModal.id = "strmpr-image-modal";
    document.body.appendChild(imageModal);

    // Setup modal event listeners
    const closeBtn = imageModal.querySelector(".strmpr-modal-close");
    closeBtn.addEventListener("click", () => this.hideImageModal());

    imageModal.addEventListener("click", (event) => {
      if (event.target === imageModal) {
        this.hideImageModal();
      }
    });
  }

  /**
   * Show image modal
   */
  showImageModal(imageSrc) {
    const imageModal = document.getElementById("strmpr-image-modal");
    const modalImg = imageModal?.querySelector(".strmpr-modal-img");

    if (modalImg && imageModal) {
      modalImg.src = imageSrc;
      imageModal.style.display = "block";
    }
  }

  /**
   * Hide image modal
   */
  hideImageModal() {
    const imageModal = document.getElementById("strmpr-image-modal");
    if (imageModal) {
      imageModal.style.display = "none";
    }
  }

  /**
   * Handle map design - adjust grid row end based on search state
   */
  handleMapDesign() {
    const { mapContainer } = this.elements;
    const errorElement = document.getElementById("storemapper-error");
    const storemapperList = document.getElementById("storemapper-list");

    if (!mapContainer) return;

    // Primary check: Set grid row end based on storemapper-list children count
    if (storemapperList) {
      if (storemapperList.children.length <= 1) {
        mapContainer.style.gridRowEnd = "5";
      } else {
        mapContainer.style.gridRowEnd = "4";
      }
    }

    // Set grid row end if there's an error message
    if (errorElement && errorElement.innerHTML.trim() !== "") {
      mapContainer.style.gridRowEnd = "5";
    }
  }

  /**
   * Handle map design on search button click
   */
  handleMapDesignOnSearch() {
    const { mapContainer } = this.elements;
    if (!mapContainer) return;

    // Recheck storemapper-list after search with a slight delay
    setTimeout(() => {
      const storemapperList = document.getElementById("storemapper-list");
      if (storemapperList) {
        if (storemapperList.children.length <= 1) {
          mapContainer.style.gridRowEnd = "5";
        } else {
          mapContainer.style.gridRowEnd = "4";
        }
      }
    }, 100);
  }

  /**
   * Handle map design on reset button click
   */
  handleMapDesignOnReset() {
    const { mapContainer } = this.elements;
    if (!mapContainer) return;

    // After reset, list should be empty, so set to 5
    mapContainer.style.gridRowEnd = "5";
  }

  /**
   * Create element helper
   */
  createElement(tag, className = "", children = [], textContent = "") {
    const element = document.createElement(tag);

    if (className) {
      if (Array.isArray(className)) {
        element.classList.add(...className);
      } else {
        element.className = className;
      }
    }

    if (textContent) {
      element.innerHTML = textContent;
    }

    children.forEach((child) => {
      if (child) element.appendChild(child);
    });

    return element;
  }

  normalizeToken(str) {
    return (str || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^\w\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
 
  getActiveDropdownGroups() {
    const dropdowns = Array.from(
      document.querySelectorAll(
        ".strmpr-search-result-filters .strmpr-dropdown:not(.strmpr-dropdown-country)",
      ),
    );
 
    const activeGroups = [];
 
    dropdowns.forEach((dropdown) => {
      const { regular } = this.getDropdownCheckboxMeta(dropdown);
      if (!regular.length) return;
 
      const checkedEntries = regular.filter((entry) => entry.checkbox.checked);
 
      if (checkedEntries.length === 0 || checkedEntries.length === regular.length) {
        return;
      }
 
      const selectedOptions = checkedEntries
        .map((entry) => {
          const labelText =
            entry.label?.textContent ||
            entry.option?.textContent ||
            entry.checkbox.value ||
            "";
          return this.normalizeToken(labelText);
        })
        .filter(Boolean);
 
      const buttonSpan = dropdown.querySelector(".strmpr-dropdown-btn span");
      const groupName = this.normalizeToken(buttonSpan?.textContent || "");
 
      activeGroups.push({
        groupName,
        selectedOptions,
      });
    });
 
    return activeGroups;
  }
 
  /**
   * Extracts category tokens ONLY from custom fields and attributes (ignores address/body text)
   */
  getStoreTokens(resultDiv) {
    const tokens = new Set();
 
    const customFields = resultDiv.querySelectorAll(".strmpr-field-custom");
    customFields.forEach((cf) => {
      const text = cf.textContent.trim();
      const colonIndex = text.indexOf(":");
      
      if (colonIndex !== -1) {
        const label = this.normalizeToken(text.substring(0, colonIndex));
        if (
          label.includes("collection") ||
          label.includes("type") ||
          label.includes("product") ||
          label.includes("category") ||
          label.includes("event")
        ) {
          const rawValue = text.substring(colonIndex + 1);
          rawValue.split(/[,|\n;/]/).forEach((part) => {
            const norm = this.normalizeToken(part);
            if (norm) tokens.add(norm);
          });
        }
      }
    });
 
    const attrData = [
      resultDiv.getAttribute("data-categories"),
      resultDiv.getAttribute("data-category"),
      resultDiv.getAttribute("data-tags"),
      resultDiv.getAttribute("data-collections"),
    ]
      .filter(Boolean)
      .join(",")
      .split(/[,;]/);
 
    attrData.forEach((item) => {
      const norm = this.normalizeToken(item);
      if (norm) tokens.add(norm);
    });
 
    const collectionField = resultDiv.querySelector(
      ".strmpr_collection--list, .strmpr-field-collection",
    );
    if (collectionField) {
      const raw = collectionField.textContent.replace(/^collection(s)?\s*:\s*/i, "").trim();
      raw.split(/[,|\n;/]/).forEach((part) => {
        const norm = this.normalizeToken(part);
        if (norm) tokens.add(norm);
      });
    }
 
    return Array.from(tokens);
  }
 
  isTokenMatch(storeToken, selectedOption) {
    const sTok = this.normalizeToken(storeToken);
    const opt = this.normalizeToken(selectedOption);
    if (!sTok || !opt) return false;
    if (sTok === opt) return true;
 
    if (opt === "suit and tux" || opt === "suit tux" || opt === "suits and tuxedos") {
      const hasSuit = SUIT_TUX_RE.test(sTok);
      const hasMen = MEN_RE.test(sTok) && !WOMEN_RE.test(sTok);
      return hasSuit || hasMen;
    }
 
    if (opt === "bridal" || opt === "bride") {
      return BRIDAL_RE.test(sTok);
    }
 
    if (opt.includes("disney")) {
      const optIsTux = opt.includes("tuxedo") || opt.includes("tux");
      const sTokIsTux = sTok.includes("tuxedo") || sTok.includes("tux");
      if (optIsTux !== sTokIsTux) return false;
    }
 
    if (opt.includes("velvet") !== sTok.includes("velvet")) return false;
    if (opt.includes("studio") !== sTok.includes("studio")) return false;
 
    if (opt.includes("men") && !opt.includes("women") && sTok.includes("women")) {
      return false;
    }
 
    return sTok === opt || sTok.includes(opt) || opt.includes(sTok);
  }
 
  applyActiveFacetFilters() {
    if (this.pageType !== "find-retailer") return;
 
    const list = document.getElementById("storemapper-list");
    if (!list) return;
 
    const activeGroups = this.getActiveDropdownGroups();
    const hasActiveFilters = activeGroups.length > 0;
 
    if (hasActiveFilters) {
      list.classList.add("strmpr-facet-active");
    } else {
      list.classList.remove("strmpr-facet-active");
    }
 
    const results = Array.from(
      list.querySelectorAll(":scope > .strmpr-search-result[data-idx]"),
    );
 
    if (!results.length) return;
 
    let visibleCount = 0;
 
    results.forEach((resultDiv) => {
      const storeTokens = this.getStoreTokens(resultDiv);
 
      // AND Logic across groups; OR logic within each group
      const matchesAllGroups =
        !hasActiveFilters ||
        activeGroups.every((group) =>
          group.selectedOptions.some((selectedOption) =>
            storeTokens.some((storeToken) =>
              this.isTokenMatch(storeToken, selectedOption),
            ),
          ),
        );
 
      if (matchesAllGroups) {
        resultDiv.setAttribute("data-facet-verified", "true");
        resultDiv.classList.remove("hidden", "strmpr-facet-filtered-out");
        resultDiv.style.removeProperty("display");
        visibleCount++;
      } else {
        resultDiv.setAttribute("data-facet-verified", "false");
        resultDiv.classList.add("hidden", "strmpr-facet-filtered-out");
        resultDiv.style.setProperty("display", "none", "important");
      }
    });
 
    this.renumberVisibleResults();
    this.syncMapPinsVisibility();
    this.handleEmptyFacetState(visibleCount, hasActiveFilters);
  }
 
  renumberVisibleResults() {
    const list = document.getElementById("storemapper-list");
    if (!list) return;
 
    list.querySelectorAll(".strmpr-divider").forEach((d) => d.remove());
 
    const visibleResults = Array.from(
      list.querySelectorAll(":scope > .strmpr-search-result[data-idx]"),
    ).filter(
      (el) =>
        !el.classList.contains("strmpr-facet-filtered-out") &&
        el.getAttribute("data-facet-verified") !== "false" &&
        el.style.display !== "none",
    );
 
    visibleResults.forEach((el, index) => {
      const storeNum = el.querySelector(".store-number");
      if (storeNum) {
        storeNum.textContent = `${index + 1} `;
      }
    });
  }
 
  syncMapPinsVisibility() {
    const results = Array.from(
      document.querySelectorAll(
        "#storemapper-list .strmpr-search-result[data-idx]",
      ),
    );
    const mapPins = document.querySelectorAll("gmp-advanced-marker");
 
    results.forEach((resultDiv, index) => {
      const isHidden =
        resultDiv.classList.contains("strmpr-facet-filtered-out") ||
        resultDiv.getAttribute("data-facet-verified") === "false" ||
        resultDiv.style.display === "none";
      const idx = resultDiv.getAttribute("data-idx");
 
      if (mapPins[index]) {
        if (isHidden) {
          mapPins[index].classList.add("hidden");
          mapPins[index].style.setProperty("display", "none", "important");
        } else if (this.hasExecutedSearch) {
          mapPins[index].classList.remove("hidden");
          mapPins[index].style.removeProperty("display");
        }
      }
 
      const viewEl = document.querySelector(`.storemapper_view[data-idx="${idx}"]`);
      if (viewEl) {
        if (isHidden) {
          viewEl.style.setProperty("display", "none", "important");
        } else {
          viewEl.style.removeProperty("display");
        }
      }
    });
  }
 
  handleEmptyFacetState(visibleCount, hasActiveFilters) {
    const list = document.getElementById("storemapper-list");
    if (!list) return;
 
    let emptyMessage = list.querySelector(".strmpr-facet-empty-message");
 
    if (visibleCount === 0 && hasActiveFilters && this.hasExecutedSearch) {
      if (!emptyMessage) {
        emptyMessage = this.createElement(
          "div",
          "strmpr-facet-empty-message",
          [],
          `<p style='padding: 24px; text-align: center; color: #58514C;'>${storemapperStrings.noResultsFilters}</p>`,
        );
        list.appendChild(emptyMessage);
      }
      emptyMessage.style.display = "block";
    } else if (emptyMessage) {
      emptyMessage.style.display = "none";
    }
  }
  /**
   * Cleanup observers
   */
  destroy() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
    clearTimeout(this.functionalitiesTimeout);
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new StoreMapper();
});
